import { Component, OnInit, HostListener, TemplateRef, ViewChild, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from './_services/google-analytics.service';
import { MobileDockService } from './_services/mobile-dock.service';
import { RouteScrollService } from './_services/route-scroll.service';
import { NgbDropdown, NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isOutcomesRoute } from './_shared/utils/nav-active';


declare var gtag;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);

  
  public cookieBannerVisible: boolean = true;
  public userSurveyVisible: boolean = false;
  public currentYear: number;
  /**
   * Outcomes spans two routes: the intro page the nav links to, and the indicator
   * page it hands over to. routerLinkActive only knows about the link's own URL,
   * so the highlight is driven from the router instead and covers both.
   */
  public outcomesActive: boolean = false;
  public navbarScrolled: boolean = false;

  /** The menu behind the hamburger, below the navbar's collapse point. */
  @ViewChild('siteMenu') private siteMenu: TemplateRef<any>;
  public menuOpen: boolean = false;
  public auditExpanded: boolean = false;
  private menuRef: NgbOffcanvasRef;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.navbarScrolled = (window.scrollY || document.documentElement.scrollTop) > 20;
  }

  constructor(
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private offcanvas: NgbOffcanvas,
    private dock: MobileDockService,
    private routeScroll: RouteScrollService,
    ) {
      this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nav) => {
        if (nav instanceof NavigationEnd){
          this.outcomesActive = isOutcomesRoute(this.router.url);
        }
      })
      // NavigationEnd has usually already fired by the time this component is
      // constructed on a deep link, so seed it from the current URL too.
      this.outcomesActive = isOutcomesRoute(this.router.url);
      }

  ngOnInit() {
    this.routeScroll.start();

    const currentDate = new Date();
    this.currentYear = currentDate.getFullYear();
    const navEndEvents = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    );
    navEndEvents.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: NavigationEnd) => {
      gtag('config', 'G-6THZW56T0Z', {
        'page-path': event.urlAfterRedirects,
      });
    })

    this.checkCookieExpirationDate();
    // this.checkUserSurvey();

    // The hamburger is gone at desktop widths, so a menu left open on a rotate or a
    // resize would have no way of being closed.
    this.dock.compact$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(compact => {
      if (!compact) {
        this.closeMenu();
      }
    });
  }

  checkCookieExpirationDate() {
    var expires = localStorage.getItem("keyExpiresCVDP");

    var date = new Date();
    var n = date.toISOString();

    if(expires == null || n > expires) {
      // No stored choice (or it has expired) — ask the user
      this.cookieBannerVisible = true;
    }
    else {
      // A valid choice is stored — keep the banner hidden.
      this.cookieBannerVisible = false;
      // Only re-apply analytics consent if the user previously agreed.
      if (localStorage.getItem("cookieConsentCVDP") === "granted") {
        this.grantAnalyticsConsent();
      }
    }
  }

  setCookieExpirationDate(consent: string) {
    // remember the choice for 1 year from now
    var expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = [
        "key=value",
        "expires=" + expires.toUTCString()
    ].join("; ");
    // store the expiry and the choice in local storage
    if (window.localStorage) {
        localStorage.setItem("keyExpiresCVDP", expires.toISOString());
        localStorage.setItem("cookieConsentCVDP", consent);
    }
  }

  grantAnalyticsConsent() {
    gtag('consent', 'update', {
      'ad_storage': 'granted',
      'analytics_storage': 'granted',
    });
    gtag('config', 'G-6THZW56T0Z');
  }

  allowCookies() {
    this.grantAnalyticsConsent();
    this.cookieBannerVisible = false;
    this.setCookieExpirationDate('granted');
  }

  dismissCookies() {
    this.cookieBannerVisible = false;
    this.setCookieExpirationDate('dismissed');
  }

  hasRoute(route: string) {
    return this.router.url.includes(route);
  }

  // Menu, below the navbar's collapse point ---------------------------------

  public toggleMenu(): void {
    if (this.menuOpen) {
      this.closeMenu();
      return;
    }
    this.menuOpen = true;
    this.auditExpanded = false;
    this.menuRef = this.offcanvas.open(this.siteMenu, {
      position: 'end',
      panelClass: 'cvdp-drawer',
      backdropClass: 'cvdp-drawer-backdrop',
      ariaLabelledBy: 'cvdpMenuTitle'
    });
    // Closed, dismissed, Escape and the backdrop all settle the same way.
    const finished = () => {
      this.menuOpen = false;
      this.menuRef = null;
    };
    this.menuRef.result.then(finished, finished);
  }

  public closeMenu(): void {
    this.menuRef?.dismiss();
  }

  linkToFeedbackForm() {
    const feedbackFormUrl = 'https://forms.cloud.microsoft/pages/responsepage.aspx?id=XkbLdzLL5EazzNQ9ClWpNq7d2NJAT9BPgPHPZT2XJHFUQ0hTTUtJNlBFUzhKQ0o3NTBGODFBMEoxUiQlQCN0PWcu&route=shorturl';
    window.open(feedbackFormUrl, '_blank');
    // Collect data for Google Analytics
    this.googleAnalyticsService.feedbackEvent("Feedback", "click");
  }

  // Collect data for Google Analytics
  privacyDataCollection() {
    this.googleAnalyticsService.privacyPolicyEvent("Privacy Policy", "click");
    this.router.navigate(['/privacy-policy']);
  }

  private dropdownCloseTimer?: ReturnType<typeof setTimeout>;

  over(drop: NgbDropdown) {
    if (this.dropdownCloseTimer) clearTimeout(this.dropdownCloseTimer);
    drop.open();
  }
  out(drop: NgbDropdown) {
    if (this.dropdownCloseTimer) clearTimeout(this.dropdownCloseTimer);
    // Small delay so moving the mouse from the toggle to the menu (across any gap)
    // doesn't immediately close the dropdown.
    this.dropdownCloseTimer = setTimeout(() => drop.close(), 250);
  }

  // User survey
  // public setUserSurvey(): void {
  //   const expirationDate = new Date();
  //   expirationDate.setMonth(expirationDate.getMonth() + 6);

  //   const preferenceData = {
  //     value: 'cvdpSurveyExp',
  //     expirationDate: expirationDate.toISOString(),
  //   };

  //   localStorage.setItem('cvdpSurveyExp', JSON.stringify(preferenceData));

  //   // Open survey
  //   window.open("https://airtable.com/shrv0U3x90yPloQVO", "_blank");

  //   // Remove pop-up
  //   this.userSurveyVisible = false;
  // }

  // private checkUserSurvey(): any {
  //   const preferenceData = JSON.parse(localStorage.getItem('cvdpSurveyExp'));

  //   if (preferenceData && preferenceData.expirationDate) {
  //     const expirationDate = new Date(preferenceData.expirationDate);

  //     if (expirationDate > new Date()) {
  //       this.userSurveyVisible = false;
  //     } else {
  //       this.userSurveyVisible = true;
  //       localStorage.removeItem('cvdpSurveyExp');
  //     }
  //   } else {
  //     this.userSurveyVisible = true;
  //   }
  // }

}
